/**
 * Re-links the /create gallery selection to the 3-tier prompt taxonomy.
 *
 * The gallery presents THEME tiles keyed by a public slug (`hobbies.gallery_slug`,
 * e.g. `golden`), which is spelled differently from the theme id
 * (`hobbies.id`, e.g. `golden_retriever`). The slug→theme bridge lives on the
 * theme row and reaches the client through `get_public_program_by_slug`
 * (every tile carries `theme_id` + `sub_style_id`). There is deliberately NO
 * hand-written slug map here any more — a map that can disagree with the tile
 * list will disagree again, silently (mem://architecture/one-list-taxonomy).
 *
 * `generate-avatar` composes its prompt from three layers:
 *   1. style (look)   — `bitmoji_inspired`, carries the cutout instruction
 *   2. sub-style      — dogs | cats | sports | hobbies
 *   3. theme          — `hobbies.id`
 */

/** Base Cardmoji style that carries the cutout prompt when none is configured. */
export const DEFAULT_BASE_STYLE_ID = "bitmoji_inspired";

export interface TaxonomyTriple {
  styleId: string;
  subStyleId: string | null;
  themeId: string | null;
}

export interface ProgramTileLike {
  style_id: string;
  theme_id?: string | null;
  sub_style_id?: string | null;
}

export class UnknownGallerySlugError extends Error {
  constructor(public readonly slug: string) {
    super(`Gallery slug "${slug}" is not a tile of this programme`);
    this.name = "UnknownGallerySlugError";
  }
}

/**
 * Resolve a stored gallery pick into the taxonomy triple that generate-avatar
 * needs.
 *
 * - A pick that already carries `hobby_id` (written by the current gallery) is
 *   passed through.
 * - A legacy pick (in-flight draft from an older bundle: slug only) is looked
 *   up in the programme's tile list, which is derived from the theme table.
 * - An unknown slug THROWS. It used to fall back to "treat as a base style",
 *   which produced a weaker image with no error — the silent-failure mode this
 *   module exists to remove.
 */
export function resolveTaxonomyPick(
  pick: { style_id: string; sub_style_id?: string | null; hobby_id?: string | null } | null | undefined,
  programConfig?: Record<string, any> | null,
  programTiles?: ProgramTileLike[] | null,
): TaxonomyTriple | null {
  if (!pick?.style_id) return null;

  const baseStyleId = String(programConfig?.baseStyleId ?? DEFAULT_BASE_STYLE_ID);

  if (pick.hobby_id) {
    return {
      styleId: baseStyleId,
      subStyleId: pick.sub_style_id ?? "hobbies",
      themeId: pick.hobby_id,
    };
  }

  const tile = (programTiles ?? []).find((t) => t.style_id === pick.style_id);
  if (!tile?.theme_id) throw new UnknownGallerySlugError(pick.style_id);

  return {
    styleId: baseStyleId,
    subStyleId: pick.sub_style_id ?? tile.sub_style_id ?? "hobbies",
    themeId: tile.theme_id,
  };
}
